import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Job } from '../services/api';
import { formatCredits, CREDITS_TOOLTIP } from '../utils/format';
import { OcrCostBreakdown, ocrCostTooltipTitle } from './OcrCostBreakdown';
import {
  getS3UploadsConsoleUrl,
  getS3OutputConsoleUrl,
  getS3OcrInputConsoleUrl,
  getS3AiResponseConsoleUrl,
  getCloudWatchLogsUrl,
} from '../config/aws-console';

/**
 * Shared runs/jobs table. Renders the same rich column set + expandable detail
 * rows used on the user-detail page, reused by the organization-detail page.
 *
 * - `showUser` adds a User column (org spans many users; the per-user page hides it).
 * - Rows accept the `Job` shape from the admin API (nested tokenUsage + progress).
 * - Owns its own copy-to-clipboard snackbar so it's drop-in self-contained.
 */
export interface JobRunsTableProps {
  jobs: Job[];
  /** Show a User column (email, click → onUserClick). Default false. */
  showUser?: boolean;
  /** Called when the User cell is clicked (e.g. navigate to the user page). */
  onUserClick?: (userId: string) => void;
  /** Message when there are no jobs. */
  emptyMessage?: string;
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'PROCESSING') return 'warning';
  if (status === 'QUEUED') return 'info';
  return 'default';
}

const JobRunsTable: React.FC<JobRunsTableProps> = ({ jobs, showUser = false, onUserClick, emptyMessage = 'No runs found' }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const toggleRowExpansion = (jobId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(label);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (jobs.length === 0) {
    return <Typography color="text.secondary">{emptyMessage}</Typography>;
  }

  // expand + jobId + [user] + type + status + procTime + model + cost + credits + in + out + thinking + progress + created
  const colSpan = showUser ? 14 : 13; // includes the Credits column (Trello #202)

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell>Job ID</TableCell>
              {showUser && <TableCell>User</TableCell>}
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Processing Time</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">Cost ($)</TableCell>
              <TableCell align="right">
                <Tooltip title={CREDITS_TOOLTIP} arrow>
                  <span>Credits</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Input Tokens</TableCell>
              <TableCell align="right">Output Tokens</TableCell>
              <TableCell align="right">
                <Tooltip title="Estimated thinking tokens (chars ÷ 3.5). Anthropic bundles thinking into output_tokens — chars are the actual signal." arrow>
                  <span>Thinking (est)</span>
                </Tooltip>
              </TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <React.Fragment key={job.jobId}>
                <TableRow hover sx={{ '& > *': { borderBottom: expandedRows.has(job.jobId) ? 'none' : undefined } }}>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleRowExpansion(job.jobId)} sx={{ padding: '4px' }}>
                      {expandedRows.has(job.jobId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {job.jobId.substring(0, 8)}...
                  </TableCell>
                  {showUser && (
                    <TableCell
                      sx={onUserClick ? { cursor: 'pointer', color: 'primary.main' } : undefined}
                      onClick={onUserClick ? () => onUserClick(job.userId) : undefined}
                    >
                      {job.userEmail || job.userId}
                    </TableCell>
                  )}
                  <TableCell>{job.jobTypeDisplayName || job.jobType}</TableCell>
                  <TableCell>
                    <Chip label={job.status} size="small" color={statusColor(job.status)} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>
                    {job.processingTimeMs ? formatDuration(job.processingTimeMs) : '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {job.tokenUsage?.model || '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {/* Trello #317 — Cost is the true all-in total: LLM token cost +
                        Google Vision OCR cost. When OCR contributed, show the total
                        with a tooltip breakdown; an upload row (no tokenUsage but a
                        real OCR cost) now shows its cost instead of a bare "-". */}
                    {(job.tokenUsage?.costUsd !== undefined || job.ocrCostUsd !== undefined) ? (
                      job.ocrCostUsd ? (
                        <Tooltip arrow title={ocrCostTooltipTitle(job.tokenUsage?.costUsd ?? 0, job.ocrCostUsd, job.ocrPages ?? 0)}>
                          <span>${((job.tokenUsage?.costUsd ?? 0) + job.ocrCostUsd).toFixed(4)}</span>
                        </Tooltip>
                      ) : (
                        <span>${(job.tokenUsage?.costUsd ?? 0).toFixed(4)}</span>
                      )
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatCredits(job.creditsCharged)}
                  </TableCell>
                  <TableCell align="right">
                    {job.tokenUsage?.inputTokens ? job.tokenUsage.inputTokens.toLocaleString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {job.tokenUsage?.outputTokens ? job.tokenUsage.outputTokens.toLocaleString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {job.tokenUsage?.thinkingChars
                      ? Math.ceil(job.tokenUsage.thinkingChars / 3.5).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>{job.progress}%</TableCell>
                  <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={colSpan}>
                    <Collapse in={expandedRows.has(job.jobId)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">Job ID</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{job.jobId}</Typography>
                            <Tooltip title="Copy full Job ID" arrow>
                              <IconButton size="small" onClick={() => copyToClipboard(job.jobId, `Job ID copied: ${job.jobId.substring(0, 12)}...`)} sx={{ padding: '4px' }}>
                                <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">CloudWatch Logs</Typography>
                            <Tooltip title="Copy CloudWatch Logs Insights URL for this job" arrow>
                              <IconButton size="small" onClick={() => copyToClipboard(getCloudWatchLogsUrl(job.jobId, job.createdAt), 'CloudWatch Logs URL copied')} sx={{ padding: '4px' }}>
                                <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">S3 Uploads</Typography>
                            <Tooltip title="Copy S3 console URL for this user's uploads" arrow>
                              <IconButton size="small" onClick={() => copyToClipboard(getS3UploadsConsoleUrl(job.userId), 'S3 uploads path copied')} sx={{ padding: '4px' }}>
                                <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">S3 Output</Typography>
                            {job.outputLocation ? (
                              <Tooltip title={`Copy S3 output path: ${job.outputLocation}`} arrow>
                                <IconButton size="small" onClick={() => copyToClipboard(getS3OutputConsoleUrl(job.outputLocation!), 'S3 output path copied')} sx={{ padding: '4px' }}>
                                  <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled">-</Typography>
                            )}
                          </Box>
                          {job.outputLocation && (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">OCR Input</Typography>
                                <Tooltip title="Copy S3 URL for raw OCR input text" arrow>
                                  <IconButton size="small" onClick={() => copyToClipboard(getS3OcrInputConsoleUrl(job.outputLocation!), 'OCR input URL copied')} sx={{ padding: '4px' }}>
                                    <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">AI Response</Typography>
                                <Tooltip title="Copy S3 URL for raw AI response text" arrow>
                                  <IconButton size="small" onClick={() => copyToClipboard(getS3AiResponseConsoleUrl(job.outputLocation!), 'AI response URL copied')} sx={{ padding: '4px' }}>
                                    <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </>
                          )}
                          {/* Trello #317 — Google Vision OCR cost breakdown (shared with
                              DashboardPage; renders only when the run had paid OCR). */}
                          <OcrCostBreakdown ocrCostUsd={job.ocrCostUsd} ocrPages={job.ocrPages} />
                        </Box>
                        {job.status === 'FAILED' && job.errorMessage && (
                          <Box sx={{ mt: 2, p: 2, backgroundColor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                            <Typography variant="subtitle2" fontWeight={600} color="error.dark" gutterBottom>Error Details</Typography>
                            <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {job.errorMessage}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default JobRunsTable;
